import {type Payments, type TokenResult, type PaymentRequest, type GooglePay, type ApplePay, type PaymentRequestOptions, type StoreVerifyBuyerDetails, type ChargeVerifyBuyerDetails, type ACH, type AfterpayClearpay, type CashAppPay, type TokenizationEvent, type SqEvent, type TokenStatus, type MethodType, type PlaidEventName} from '@square/web-payments-sdk-types';
import {disableGatewayOption, FormFields, observeCheckoutHTML, onCheckoutSubmit, onCheckoutSubmitError, onCheckoutUpdate, tokenizedMethodSelected} from '../checkout';
import {Feature} from '../features';
import {$qsAll, $qs, loadScript} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import {getErrorString} from '../../../@shared/ts/error';
import {insertCaptchaFields, cleanupCaptchaFields} from '../botProtection';
import {type TransactionUpdate} from '../../../@shared/ts/transaction';
import {updatePaymentTransaction, cleanupTransactionFields, createPaymentTransaction, insertTransactionFields} from '../transaction';
import {type PeachPayCheckoutData, type PayDetails, type TokenizeSquareResult, type TokenizeFunction} from '../types';

declare const peachpay_checkout_data: PeachPayCheckoutData;

async function initSquarePayments($checkoutForm: HTMLFormElement) {
	if (!Feature.enabled(FeatureFlag.SQUARE_GATEWAYS)) {
		return;
	}

	const scriptSrc = Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'script_src');
	if (!scriptSrc) {
		return;
	}

	await loadScript(scriptSrc, 'Square');
	const applicationId = Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'application_id');
	const locationId = Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'location_id');

	if (!applicationId || !locationId || !window.Square) {
		return;
	}

	const payments = window.Square.payments(applicationId, locationId);
	const paymentRequest = createPaymentRequest(payments);

	const tokenizeFunctions = {
		peachpay_square_card: cardPayments(payments),
		peachpay_square_applepay: applepayPayments(payments, paymentRequest),
		peachpay_square_googlepay: googlepayPayments(payments, paymentRequest),
		peachpay_square_ach: achBankPayments(payments),
		peachpay_square_afterpay: afterpay(payments, paymentRequest),
		peachpay_square_cashapp: cashapp(payments, paymentRequest),
	};

	onCheckoutSubmit($checkoutForm, tokenizeFunctions);
	onCheckoutSubmitError($checkoutForm, tokenizeFunctions, async (transactionId, errors) => {
		cleanupTokenFields();
		if (!transactionId) {
			return;
		}

		try {
			await updatePaymentTransaction(transactionId, {
				paymentStatus: 'unperformed',
				orderStatus: 'creationfailed',
				note: errors.join('\n'),
			});
		} catch {
			// Do no harm.
		}
	});
}

function insertTokenFields($checkoutForm: HTMLFormElement, sourceId?: string, verificationId?: string | null) {
	if (sourceId) {
		const $sourceId = document.createElement('input');
		$sourceId.type = 'hidden';
		$sourceId.name = 'peachpay_square_source_id';
		$sourceId.value = sourceId;
		$checkoutForm.insertAdjacentElement('beforeend', $sourceId);
	}

	if (verificationId) {
		const $verificationId = document.createElement('input');
		$verificationId.type = 'hidden';
		$verificationId.name = 'peachpay_square_verification_id';
		$verificationId.value = verificationId;
		$checkoutForm.insertAdjacentElement('beforeend', $verificationId);
	}
}

function cleanupTokenFields() {
	$qsAll('input[name="peachpay_square_source_id"]', $el => {
		$el.remove();
	});
	$qsAll('input[name="peachpay_square_verification_id"]', $el => {
		$el.remove();
	});
}

function displayTokenizeError(message: string) {
	if (!message) {
		return;
	}

	// Target only the payment box not hidden.
	const target = '.payment_box:not([style*="display:none"]):not([style*="display: none"])';

	$qsAll(`${target} .peachpay-tokenize-error`, $el => {
		$el.classList.remove('hide');
		$el.innerHTML = message;

		$el.scrollIntoView();
	});

	setTimeout(() => {
		$qsAll('.peachpay-tokenize-error', $el => {
			$el.classList.add('hide');
			$el.innerHTML = '';
		});
	}, 10000);
}

function createPaymentRequest(payments: Payments) {
	const options = () => {
		if (peachpay_checkout_data.page.is_order_pay) {
			const order_pay_details = peachpay_checkout_data.order_pay_details as PayDetails;

			const data: PaymentRequestOptions = {
				countryCode: Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'country') ?? 'US',
				currencyCode: order_pay_details?.currency,
				discounts: order_pay_details.discount_lines.map(l => ({
					label: l.label,
					amount: Number(l.amount).toFixed(2),
				})),
				lineItems: order_pay_details.item_lines.map(l => ({
					label: l.label,
					amount: Number(l.total).toFixed(2),
				})),

				taxLineItems: order_pay_details.tax_lines.map(l => ({
					label: l.label,
					amount: Number(l.amount).toFixed(2),
				})),
				total: {
					label: order_pay_details.total_line.label,
					amount: Number(order_pay_details.total_line.amount).toFixed(2),
				},
			};

			return data;
		}

		const data: PaymentRequestOptions = {
			countryCode: Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'country') ?? 'US',
			currencyCode: peachpay_checkout_data.cart_details.currency,
			discounts: peachpay_checkout_data.cart_details.discount_lines.map(l => ({
				label: l.label,
				amount: Number(l.amount).toFixed(2),
			})),
			lineItems: peachpay_checkout_data.cart_details.item_lines.map(l => ({
				label: l.label,
				amount: Number(l.amount).toFixed(2),
			})),

			taxLineItems: peachpay_checkout_data.cart_details.tax_lines.map(l => ({
				label: l.label,
				amount: Number(l.amount).toFixed(2),
			})),
			total: {
				label: peachpay_checkout_data.cart_details.total_line.label,
				amount: Number(peachpay_checkout_data.cart_details.total_line.amount).toFixed(2),
			},
		};

		return data;
	};

	const paymentRequest = payments.paymentRequest(options());
	// Notifies opened digital wallets to close to ensure payment request update succeeds
	const paymentRequestUpdateEvent = new Event('pp-square-payment-request-update');
	onCheckoutUpdate(() => {
		window.dispatchEvent(paymentRequestUpdateEvent);
		paymentRequest.update(options());
	});

	return paymentRequest;
}

function createVerifyBuyerDetails(): StoreVerifyBuyerDetails | ChargeVerifyBuyerDetails {
	const billingContact = {
		addressLines: [
			$qs<HTMLInputElement>('[name="billing_address_1"]')?.value ?? '',
			$qs<HTMLInputElement>('[name="billing_address_2"]')?.value ?? '',
		],
		email: $qs<HTMLInputElement>('[name="billing_email"]')?.value ?? '',
		familyName: $qs<HTMLInputElement>('[name="billing_last_name"]')?.value ?? '',
		givenName: $qs<HTMLInputElement>('[name="billing_first_name"]')?.value ?? '',
		city: $qs<HTMLInputElement>('[name="billing_city"]')?.value ?? '',
		countryCode: $qs<HTMLInputElement>('[name="billing_country"]')?.value ?? Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'country') ?? '',
		phone: $qs<HTMLInputElement>('[name="billing_phone"]')?.value ?? '',
		state: $qs<HTMLInputElement>('[name="billing_state"]')?.value ?? '',
		postalCode: $qs<HTMLInputElement>('[name="billing_postcode"]')?.value ?? '',
	};

	if (Number(peachpay_checkout_data.cart_details.total_line.amount) > 0) {
		return {
			intent: 'CHARGE',
			amount: Number(peachpay_checkout_data.cart_details.total_line.amount).toFixed(2),
			currencyCode: peachpay_checkout_data.cart_details.currency,
			billingContact,
		};
	}

	return {
		intent: 'STORE',
		billingContact,
	};
}

function handleTokenResult(result: TokenResult): [sourceId: string | null, verificationId: string | null, update: TransactionUpdate] {
	const extractErrorMessage = (): string => {
		let message = '';
		result?.errors?.forEach(error => {
			message += error.message + '. ';
		});

		return message;
	};

	const message = extractErrorMessage();
	displayTokenizeError(message);

	switch (result.status) {
		case 'OK': {
			if (!result.token) {
				return [null, null, {}];
			}

			return [result.token, null, {}];
		}

		case 'Cancel':
			displayTokenizeError('Customer canceled the payment.');
			return [null, null, {paymentStatus: 'canceled', note: 'Customer canceled the payment.'}];
		case 'Abort':
			return [null, null, {paymentStatus: 'creationfailed', note: 'Tokenize status "Abort": ' + message}];
		case 'Error':
			return [null, null, {paymentStatus: 'creationfailed', note: message}];
		case 'Invalid':
			return [null, null, {paymentStatus: 'creationfailed', note: message}];
		case 'Unknown':
		default:
			return [null, null, {paymentStatus: 'creationfailed', note: 'Tokenize status "Unknown": ' + message}];
	}
}

async function paymentFlow(methodCallback: () => Promise<TokenizeSquareResult>): Promise<TokenizeFunction> {
	return async ($form: HTMLFormElement, gateway: string) => {
		cleanupTransactionFields();
		cleanupTokenFields();
		cleanupCaptchaFields();

		if (!tokenizedMethodSelected(gateway)) {
			const [sourceId, verificationId, update] = await methodCallback();

			if (!sourceId) {
				const transactionId = await createPaymentTransaction(gateway);
				if (!transactionId) {
					return false;
				}

				await updatePaymentTransaction(transactionId, update);
				return false;
			}

			insertTokenFields($form, sourceId, verificationId);
		}

		const transactionId = await createPaymentTransaction(gateway);
		if (!transactionId) {
			return false;
		}

		insertTransactionFields($form, transactionId);
		await insertCaptchaFields($form);

		return true;
	};
}

async function cardPayments(payments: Payments): Promise<TokenizeFunction | null> {
	if (!Feature.enabled(FeatureFlag.SQUARE_CARD_GATEWAY)) {
		observeCheckoutHTML(disableGatewayOption('peachpay_square_card'));
		return null;
	}

	const cardElement = await payments.card();
	const $container = $qs('#pp-square-card-element');

	if ($container?.innerHTML === '') {
		try {
			// If this is the first time then it has never been attached and will throw
			await cardElement.attach($container);
		} catch {
			await cardElement.detach();
		}
	}

	observeCheckoutHTML(async () => {
		const $container = $qs('#pp-square-card-element');
		if (!$container) {
			return;
		}

		if ($container.innerHTML === '') {
			try {
				// If this is the first time then it has never been attached and will throw
				await cardElement.attach($container);
			} catch {
				await cardElement.detach();
			}

			await cardElement.attach($container);
		}
	});

	return paymentFlow(async (): Promise<TokenizeSquareResult> => {
		try {
			const [sourceId, _, update] = handleTokenResult(await cardElement.tokenize());

			if (!sourceId) {
				return [null, null, update];
			}

			const result = await payments.verifyBuyer(sourceId, createVerifyBuyerDetails());

			const verificationId = result?.token ?? null;

			return [sourceId, verificationId, update];
		} catch (error: unknown) {
			const note = error instanceof Error ? error.message : 'Sorry something went wrong. Please refresh the page and try again.';
			displayTokenizeError(note);

			if (error instanceof Error) {
				return [null, null, {
					note: getErrorString(error),
				}];
			}

			return [null, null, {
				note,
			}];
		}
	});
}

async function applepayPayments(payments: Payments, paymentRequest: PaymentRequest): Promise<TokenizeFunction | null> {
	if (!Feature.enabled(FeatureFlag.SQUARE_APPLEPAY_GATEWAY)) {
		observeCheckoutHTML(disableGatewayOption('peachpay_square_applepay'));
		return null;
	}

	let apay: ApplePay | null = null;

	try {
		// The not clauses are essential as chrome on macos will include safari in the name
		if ((window as any)['ApplePaySession']) {
			apay = await payments.applePay(paymentRequest);
		}
	} catch (error) {
		console.error(error);
	}

	if (!apay) {
		// If applepay fails to initialize we should just remove it as an option. We must observe the checkout for changes in addition to ensure the
		// option does not re appear for example after selecting a new shipping option. This is also how we are preventing ApplePay from showing on
		// non safari browsers.
		observeCheckoutHTML(disableGatewayOption('peachpay_square_applepay'));
	}

	return paymentFlow(async () => handleTokenResult(await (apay!).tokenize()));
}

async function googlepayPayments(payments: Payments, paymentRequest: PaymentRequest): Promise<TokenizeFunction | null> {
	if (!Feature.enabled(FeatureFlag.SQUARE_GOOGLEPAY_GATEWAY)) {
		observeCheckoutHTML(disableGatewayOption('peachpay_square_googlepay'));
		return null;
	}

	let gpay: GooglePay | null = null;
	try {
		gpay = await payments.googlePay(paymentRequest);
	} catch {
		// If googlepay fails to initialize we should just remove it as an option. We must observe the checkout for changes in addition to ensure the
		// option does not re appear for example after selecting a new shipping option.
		observeCheckoutHTML(disableGatewayOption('peachpay_square_googlepay'));
	}

	return paymentFlow(async () => handleTokenResult(await (gpay!).tokenize()));
}

async function achBankPayments(payments: Payments): Promise<TokenizeFunction | null> {
	if (!Feature.enabled(FeatureFlag.SQUARE_ACH)) {
		observeCheckoutHTML(disableGatewayOption('peachpay_square_ach'));
		return null;
	}

	let ACHPayment: ACH | null = null;

	try {
		ACHPayment = await payments.ach({
			redirectURI: window.location.href,
			transactionId: '0',
		});

		ACHPayment.addEventListener('ontokenization' as PlaidEventName.ON_TOKENIZATION, _event => {
			// Required for plaid authentication to succeed. Event matches TokenResult from tokenize().
		});
	} catch (error) {
		observeCheckoutHTML(disableGatewayOption('peachpay_square_ach'));
	}

	return paymentFlow(async () => {
		const billingFirstName = FormFields.billing.firstName.get();
		const billingLastName = FormFields.billing.lastName.get();

		return handleTokenResult(await (ACHPayment!).tokenize({
			accountHolderName: `${billingFirstName} ${billingLastName}`,
		}));
	});
}

async function afterpay(payments: Payments, paymentRequest: PaymentRequest): Promise<TokenizeFunction | null> {
	if (!Feature.enabled(FeatureFlag.SQUARE_AFTERPAY)) {
		observeCheckoutHTML(disableGatewayOption('peachpay_square_afterpay'));
		return null;
	}

	let afterpay: AfterpayClearpay | null = null;

	const attach = async () => {
		const $container = $qs('#pp-square-afterpay-element');
		if (!$container || !afterpay) {
			return;
		}

		if ($container.innerHTML === '') {
			try {
				// If this is the first time then it has never been attached and will throw
				await afterpay.detach();
			} catch {
				// Do no harm
			}

			await afterpay.attach('#pp-square-afterpay-element');
		}
	};

	try {
		afterpay = await payments.afterpayClearpay(paymentRequest);
		// afterpay can take quite long to initialize such that HTML change events might have been missed and afterpay wasn't attached.
		await attach();
	} catch {
		if (!hasFallbackCurrencyButton('peachpay_square_afterpay')) {
			observeCheckoutHTML(disableGatewayOption('peachpay_square_afterpay'));
		}
	}

	observeCheckoutHTML(async () => {
		await attach();
	});

	return paymentFlow(async () => handleTokenResult(await (afterpay!).tokenize()));
}

async function cashapp(payments: Payments, paymentsRequest: PaymentRequest): Promise<TokenizeFunction | null> {
	if (!Feature.enabled(FeatureFlag.SQUARE_CASHAPP)) {
		observeCheckoutHTML(disableGatewayOption('peachpay_square_cashapp'));
		return null;
	}

	let cashapp: CashAppPay | null = null;

	const attach = async () => {
		const $container = $qs('#pp-square-cashapp-element');
		if (!$container || !cashapp) {
			return;
		}

		if ($container.innerHTML === '') {
			try {
				// If this is the first time then it has never been attached and will throw
				await cashapp.detach();
			} catch {
				// Do no harm
			}

			await cashapp.attach('#pp-square-cashapp-element');
		}
	};

	let cashAppTokenResult: TokenResult | null = null;

	try {
		cashapp = await payments.cashAppPay(paymentsRequest, {
			redirectURL: window.location.href,
			referenceId: 'square_cashapp_button',
		});

		cashapp?.addEventListener('ontokenization', (e: SqEvent<TokenizationEvent>) => {
			if (e.detail?.tokenResult) {
				cashAppTokenResult = e.detail.tokenResult;
				displayTokenizeError('Click the Place Order button to complete the purchase');
			}
		});

		window.addEventListener('pp-square-payment-request-update', async () => {
			/* CashApp is considered "open" when attached. Per square documentation
			 * paymentRequest.update() will fail if a digital wallet is "open".
			 * We must detach cashapp before updating the payment request otherwise it will fail.
			 */
			await cashapp?.detach();
		});

		await attach();
	} catch {
		if (!hasFallbackCurrencyButton('peachpay_square_cashapp')) {
			observeCheckoutHTML(disableGatewayOption('peachpay_square_cashapp'));
		}
	}

	observeCheckoutHTML(async () => {
		await attach();
	});

	return paymentFlow(async () => {
		if (cashAppTokenResult === null) {
			return handleTokenResult({
				status: 'Error' as TokenStatus.ERROR,
				details: {
					method: 'Cash App Pay' as MethodType.CASH_APP_PAY,
				},
				errors: [
					new Error('Please attach a Cash App account to withdrawal funds from'),
				],
			});
		}

		return handleTokenResult(cashAppTokenResult);
	});
}

function hasFallbackCurrencyButton(gateway: string) {
	const $option = document.querySelector('li.wc_payment_method.payment_method_' + gateway);

	if (!$option) {
		return false;
	}

	const $fallbackButton = $option.querySelector('button.currency-fallback-button');

	if (!$fallbackButton) {
		return false;
	}

	return true;
}

export {
	initSquarePayments,
};
